import { Users, MessageSquare, Package, Leaf } from 'lucide-react';
import { MetricCard } from './analytics/MetricCard';
import { ClassificationFilters } from './analytics/ClassificationFilters';
import { SpeciesBarChart } from './analytics/SpeciesBarChart';
import { ProductBarChart } from './analytics/ProductBarChart';
import { QueryParametersChart } from './analytics/QueryParametersChart';
import { CountryChart } from './analytics/CountryChart';
import { TopUsersChart } from './analytics/TopUsersChart';
import { AnalyticsSkeleton } from './analytics/AnalyticsSkeleton';
import { useAnalytics } from '../hooks/useAnalytics';

export const AnalyticsDashboard = () => {
  const {
    loading,
    error,
    filters,
    setFilters,
    filterOptions,
    kpis,
    speciesData,
    productData,
    queryData,
    countryData,
    topUsersData,
  } = useAnalytics();

  if (loading) return <AnalyticsSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="text-sm text-red-600 font-medium">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-xs text-slate-500 underline hover:text-slate-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Total Conversations"
          value={kpis.totalConversations.toLocaleString()}
          subtitle="Filtered conversations"
          icon={<MessageSquare className="h-4 w-4" />}
        />
        <MetricCard
          label="Unique Users"
          value={kpis.uniqueUsers.toLocaleString()}
          subtitle="Distinct accounts"
          icon={<Users className="h-4 w-4" />}
        />
        <MetricCard
          label="Unique Products"
          value={kpis.uniqueProducts.toString()}
          subtitle="Product lines consulted"
          icon={<Package className="h-4 w-4" />}
        />
        <MetricCard
          label="Top Species"
          value={kpis.topSpecies}
          subtitle="Most consulted"
          icon={<Leaf className="h-4 w-4" />}
        />
      </div>

      {/* Filters */}
      <ClassificationFilters
        filters={filters}
        options={filterOptions}
        onChange={setFilters}
      />

      {/* Species + Products */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SpeciesBarChart
          data={speciesData}
          drillLabel={filters.species}
          onBack={() => setFilters((f) => ({ ...f, species: null, productiveType: null }))}
        />
        <ProductBarChart
          data={productData}
          drillLabel={filters.productLine}
          onBack={() => setFilters((f) => ({ ...f, productLine: null, productName: null }))}
        />
      </div>

      {/* Query Parameters */}
      <QueryParametersChart data={queryData} />

      {/* Country + Top Users */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CountryChart data={countryData} />
        <TopUsersChart data={topUsersData} />
      </div>
    </div>
  );
};
